import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X, Loader2, MapPin, ShoppingBag, Calendar, Newspaper, Tag, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { newsApi } from '@/services/api'
import { US_STATES } from '@/components/us-map'
import { isAuthenticated } from '@/lib/utils'

export function FilterSidebar({ onFilterChange, activeTab }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedSources, setSelectedSources] = useState([])
  const [dateRange, setDateRange] = useState(null)
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [selectedStates, setSelectedStates] = useState([])
  const [showPurchasedOnly, setShowPurchasedOnly] = useState(false)

  // Fetch categories (tags) from API
  const { data: tagsData, isLoading: tagsLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => newsApi.getTags(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch sources (providers) from API
  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: () => newsApi.getProviders(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const categories = tagsData?.data || []
  const sources = providersData?.data || []

  const dateRanges = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'Custom', value: 'custom' },
  ]

  const handleCategoryToggle = (categoryAlias) => {
    setSelectedCategories(prev =>
      prev.includes(categoryAlias)
        ? prev.filter(c => c !== categoryAlias)
        : [...prev, categoryAlias]
    )
  }

  const handleSourceToggle = (sourceAlias) => {
    setSelectedSources(prev =>
      prev.includes(sourceAlias)
        ? prev.filter(s => s !== sourceAlias)
        : [...prev, sourceAlias]
    )
  }

  const handleApplyFilters = () => {
    // Convert dateRange to date object format expected by backend
    let dateFilter = null
    if (dateRange) {
      const today = new Date()
      const formatDate = (date) => date.toISOString().split('T')[0] // YYYY-MM-DD
      
      if (dateRange === 'today') {
        dateFilter = {
          startDate: formatDate(today),
          endDate: null
        }
      } else if (dateRange === 'week') {
        const weekAgo = new Date(today)
        weekAgo.setDate(today.getDate() - 7)
        dateFilter = {
          startDate: formatDate(weekAgo),
          endDate: formatDate(today)
        }
      } else if (dateRange === 'custom' && (customStartDate || customEndDate)) {
        dateFilter = {
          startDate: customStartDate || null,
          endDate: customEndDate || null
        }
      }
    }
    
    const filters = {
      searchKeyword: debouncedSearchTerm || null,
      selectedCategories: selectedCategories.length > 0 
        ? selectedCategories.map(slug => ({ alias: slug })) 
        : null,
      selectedFeeds: selectedSources.length > 0 
        ? selectedSources.map(slug => ({ alias: slug })) 
        : null,
      date: dateFilter,
      selectedStates: selectedStates.length > 0 ? selectedStates : null,
      purchased: showPurchasedOnly || null,
    }
    // Only send non-null filters
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== null)
    )
    onFilterChange(Object.keys(cleanFilters).length > 0 ? cleanFilters : null)
  }

  const handleResetFilters = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedCategories([])
    setSelectedSources([])
    setDateRange(null)
    setCustomStartDate('')
    setCustomEndDate('')
    setSelectedStates([])
    setShowPurchasedOnly(false)
    onFilterChange(null)
  }

  const hasActiveFilters = searchTerm || selectedCategories.length > 0 || 
                          selectedSources.length > 0 || dateRange ||
                          selectedStates.length > 0 || showPurchasedOnly

  // Debounce search term to avoid API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500) // Wait 500ms after user stops typing

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Auto-apply filters whenever any filter value changes (using debounced search)
  useEffect(() => {
    handleApplyFilters()
  }, [debouncedSearchTerm, selectedCategories, selectedSources, dateRange, customStartDate, customEndDate, selectedStates, showPurchasedOnly])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Filters</h2>
          <Badge variant="outline" className="text-xs">
            Auto-apply
          </Badge>
        </div>
        {hasActiveFilters && (
          <Button
            onClick={handleResetFilters}
            variant="ghost"
            size="sm"
            className="text-sm flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </Button>
        )}
      </div>

      {/* Search */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Search className="w-4 h-4" />
          Search
          {searchTerm && searchTerm !== debouncedSearchTerm && (
            <span className="text-xs text-gray-500">typing...</span>
          )}
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search articles..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      {/* Date Range */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Time Period
        </label>
        <div className="flex flex-col gap-2">
          {dateRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => setDateRange(range.value === dateRange ? null : range.value)}
              className={`w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                dateRange === range.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        
        {/* Custom Date Inputs */}
        {dateRange === 'custom' && (
          <div className="mt-3 space-y-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* State Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <MapPin className="w-4 h-4" />
          Filter by State
        </label>
        <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
          {US_STATES.map((state) => {
            const isSelected = selectedStates.some((s) => s[0] === state.id)
            return (
              <label
                key={state.id}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {
                    if (isSelected) {
                      setSelectedStates(selectedStates.filter((s) => s[0] !== state.id))
                    } else {
                      setSelectedStates([...selectedStates, [state.id, state.name]])
                    }
                  }}
                  className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {state.name} ({state.abbr})
                </span>
              </label>
            )
          })}
        </div>
      </div>

      {/* Sources */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Newspaper className="w-4 h-4" />
          Sources
        </label>
        {providersLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : sources.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
            {sources.map((source) => (
              <label
                key={source.alias}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedSources.includes(source.alias)}
                  onChange={() => handleSourceToggle(source.alias)}
                  className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{source.display_name || source.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No sources available</p>
        )}
      </div>

      {/* Categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Categories
        </label>
        {tagsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : categories.length > 0 ? (
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto custom-scrollbar">
            {categories.slice(0, 20).map((category) => (
              <button
                key={category.alias}
                onClick={() => handleCategoryToggle(category.alias)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                  selectedCategories.includes(category.alias)
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name || category.title}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No categories available</p>
        )}
      </div>

      {/* Purchases Filter */}
      {isAuthenticated() && (
        <div>
          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={showPurchasedOnly}
              onChange={(e) => setShowPurchasedOnly(e.target.checked)}
              className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
            />
            <div className="flex items-center gap-2 flex-1">
              <ShoppingBag className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Show only purchased articles
              </span>
            </div>
          </label>
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Active Filters:</h3>
          <div className="flex flex-wrap gap-2">
            {selectedStates.map((state) => (
              <Badge key={state[0]} variant="secondary" className="text-xs">
                {state[1]}
              </Badge>
            ))}
            {selectedCategories.map((catAlias) => {
              const category = categories.find(c => c.alias === catAlias)
              return (
                <Badge key={catAlias} variant="secondary" className="text-xs">
                  {category?.name || category?.title || catAlias}
                </Badge>
              )
            })}
            {selectedSources.map((srcAlias) => {
              const source = sources.find(s => s.alias === srcAlias)
              return (
                <Badge key={srcAlias} variant="secondary" className="text-xs">
                  {source?.display_name || source?.name || srcAlias}
                </Badge>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
